import type * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

import type * as models from '../../models'

import type { Change } from './change'
import type { Data } from './data'

export interface Chart extends Data {
  areEdgesLoaded: boolean
  /**
   * Indicates if any mutation (CDU) is in progress
   */
  isMutating?: boolean
  error?: Error | string
  metadata: Omit<ents.core.display.tree.Tree, 'nodes'>
  edits: Partial<
    Record<ents.core.display.tree.Node.Id, ents.core.display.tree.Node>
  >
  currentlyEditingNodeId?: ents.core.display.tree.Node.Id
  history: readonly Change[]
  /**
   * How many undos have been applied against the current {@link history}
   */
  undoCount: number
  /**
   * Employee insights keyed by {@link ents.core.display.tree.Node.Id}, which in
   * this context represents the employeeId tied to the org-chart node.
   */
  peopleInsightsLookup: Partial<
    Record<
      ents.core.display.tree.Node.Id,
      ents.core.display.tree.Node.PeopleInsights
    >
  >
}
export namespace Chart {
  export type Id<
    TPrefix extends models.widget.Ref.Id = models.widget.Ref.Id,
    TEntry extends Entry = Entry,
  > = `${TPrefix}${typeof Id.SEPARATOR}${TEntry}`
  export namespace Id {
    export const isId = TypeChecker.Factory.custom(
      (val): val is Id => {
        if (!TypeChecker.isString(val)) {
          return false
        }

        // Use regex to match pattern: prefix.separator.entry where prefix can contain dots
        const regex = new RegExp(`^(.+)\\${SEPARATOR}(${Entry.ALL.join('|')})$`)
        const match = val.match(regex)

        return (
          match !== null &&
          TypeChecker.isString(match[1]) && // prefix part
          Entry.ALL.includes(match[2] as Entry) // entry part
        )
      },
      { typeDescription: 'Chart.Id' },
    )
    export const SEPARATOR = '.'
    /** TODO:  */
    export function from<TPrefix extends models.widget.Ref.Id>(
      widgetRefId: TPrefix,
    ): Id<TPrefix, 'current'>
    export function from<
      TPrefix extends models.widget.Ref.Id,
      TEntry extends Entry,
    >(params: from.Params<TPrefix, TEntry>): Id<TPrefix, TEntry>
    export function from<
      TPrefix extends models.widget.Ref.Id,
      TEntry extends Entry,
    >(
      paramsOrWidgetRefId: from.Params<TPrefix, TEntry> | TPrefix,
    ): Id<TPrefix, TEntry> {
      return (
        typeof paramsOrWidgetRefId === 'string'
          ? `${paramsOrWidgetRefId}.current`
          : `${paramsOrWidgetRefId.widgetRefId}.${paramsOrWidgetRefId.entry}`
      ) as Id<TPrefix, TEntry>
    }
    export namespace from {
      export interface Params<
        TPrefix extends models.widget.Ref.Id,
        TEntry extends Entry,
      > {
        widgetRefId: TPrefix
        entry?: TEntry
      }
    }

    /** TODO:  */
    export function getParts<
      TPrefix extends models.widget.Ref.Id = models.widget.Ref.Id,
      TEntry extends Entry = Entry,
    >(id: Id<TPrefix, TEntry>): [TPrefix, TEntry] {
      const regex = new RegExp(`^(.+)\\${SEPARATOR}(${Entry.ALL.join('|')})$`)
      const match = id.match(regex)

      if (!match) {
        throw new Error(`Invalid Chart.Id format: ${id}`)
      }

      return [match[1] as TPrefix, match[2] as TEntry]
    }
  }

  export interface Identifier<
    TPrefix extends models.widget.Ref.Id = models.widget.Ref.Id,
    TEntry extends Entry = Entry,
  > {
    readonly chartId: Id<TPrefix, TEntry>
  }

  export type Lookup = Partial<Record<Id<models.widget.Ref.Id, Entry>, Chart>>
  export type Entry = 'current' | 'proposed'
  export namespace Entry {
    export const ALL = [
      'current',
      'proposed',
    ] as const satisfies readonly Entry[]
    /**
     * __NOTE:__ this type only exists as a validation check. If this has an
     * error, we are missing an ENTRY above
     */
    type EnsureWeHaveAllIds = Satisfies<Entry, (typeof ALL)[number]>

    /** TODO:  */
    export function tryGet(id: Id): Entry | undefined {
      const parts = id.split('.')
      const possibleEntry = parts[parts.length - 1] // Get the last part after splitting
      return ALL.includes(possibleEntry as Entry)
        ? (possibleEntry as Entry)
        : undefined
    }

    /** TODO:  */
    export function get(id: Id): Entry {
      const entry = tryGet(id)
      if (!entry) {
        throw new Error(`Invalid OrgChart.Entry ID: ${id}`)
      }

      return entry
    }
  }
}
